import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { Tooltip, Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ProductModal from "../../components/ProductModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_PRODUCTS") {
    const products = action.payload;
    const newProducts = [];

    products.forEach((product) => {
      const productIndex = state.findIndex((p) => p.id === product.id);
      if (productIndex !== -1) {
        state[productIndex] = product;
      } else {
        newProducts.push(product);
      }
    });

    return [...state, ...newProducts];
  }

  if (action.type === "UPDATE_PRODUCTS") {
    const product = action.payload;
    const productIndex = state.findIndex((p) => p.id === product.id);

    if (productIndex !== -1) {
      state[productIndex] = product;
      return [...state];
    } else {
      return [product, ...state];
    }
  }

  if (action.type === "DELETE_PRODUCT") {
    const productId = action.payload;
    return state.filter((product) => product.id !== productId);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  typeChip: {
    minWidth: 90,
    paddingLeft: 6,
    paddingRight: 6,
  },
  priceCell: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    width: 60,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    borderRadius: 12,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    width: 60,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    borderRadius: 12,
  },
}));

const Products = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [products, dispatch] = useReducer(reducer, []);
  const [productModalOpen, setProductModalOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  const fetchProducts = async () => {
    try {
      const { data } = await api.get("/products/list", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_PRODUCTS", payload: data.products });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-product`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PRODUCTS", payload: data.product });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PRODUCT", payload: +data.productId });
      }
    });

    return () => {
      socket.off(`company-${companyId}-product`);
    };
  }, [socketManager, user.companyId]);

  const handleOpenProductModal = () => {
    setSelectedProduct(null);
    setProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setProductModalOpen(false);
    fetchProducts();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await api.delete(`/products/${productId}`);
      toast.success(i18n.t("products.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }

    setDeletingProduct(null);
    setSearchParam("");
    dispatch({ type: "RESET" });
    setPageNumber(1);
    fetchProducts();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const getTypeIcon = (type) => {
    return type === "service" ? <LocalHospitalIcon /> : <ShoppingCartIcon />;
  };

  const getTypeLabel = (type) => {
    return type === "service" ? "Serviço" : "Produto";
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingProduct &&
          `${i18n.t("products.confirmationModal.deleteTitle")} ${
            deletingProduct.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteProduct(deletingProduct.id)}
      >
        {i18n.t("products.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ProductModal
        open={productModalOpen}
        onClose={handleCloseProductModal}
        aria-labelledby="form-dialog-title"
        productId={selectedProduct?.id}
      />
      <MainHeader>
        <Title>{i18n.t("products.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("products.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenProductModal}
          >
            {i18n.t("products.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("products.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("products.table.type")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("products.table.price")}
              </TableCell>
              <TableCell>{i18n.t("products.table.description")}</TableCell>
              <TableCell align="center">
                {i18n.t("products.table.doctorEmail")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("products.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("products.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <strong>{product.name}</strong>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={getTypeLabel(product.type)}>
                      <Chip
                        icon={getTypeIcon(product.type)}
                        label={getTypeLabel(product.type)}
                        size="small"
                        className={classes.typeChip}
                        variant="outlined"
                        color={
                          product.type === "service" ? "secondary" : "primary"
                        }
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" className={classes.priceCell}>
                    {formatPrice(product.value)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={product.description || ""}>
                      <span>
                        {product.description?.length > 50
                          ? `${product.description.substring(0, 50)}...`
                          : product.description}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {product.type === "service" && product.email ? (
                      <Tooltip title={product.email}>
                        <span>{product.email}</span>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      justifyItems: "center",
                    }}
                  >
                    <Chip
                      label={product.isAvailable ? "Ativo" : "Inativo"}
                      size="small"
                      className={
                        product.isAvailable
                          ? classes.activeChip
                          : classes.inactiveChip
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditProduct(product)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingProduct(product);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={7} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Products;
